import React from "react";
import { Alignment } from "../pdf-templates/types";
import { PdfTemplateConfiguration } from "../../context/PdfTemplateConfigurationContext";
import swissLabelLogo from "../../assets/swisslabel.png";
import szvLogo from "../../assets/szv.jpg";
import vzlsLogo from "../../assets/vzls.ico";
import { PdfTemplateType } from "../document-template.types";

/**
 * Helper function to get CSS justify-content value from alignment
 */
const getJustifyContent = (alignment: Alignment): string => {
	switch (alignment) {
		case "left":
			return "flex-start";
		case "right":
			return "flex-end";
		default:
			return "center";
	}
};

/**
 * Helper function to clean and escape HTML content
 */
const cleanHtmlContent = (html: string): string => {
	// Remove any potential script tags or dangerous content
	return html.replace(/<script[^>]*>.*?<\/script>/gi, "");
};

const isInvoice = (templateType: PdfTemplateType) => {
	return (
		templateType === PdfTemplateType.RECHNUNG ||
		templateType === PdfTemplateType.RECHNUNGSDOPPEL ||
		templateType === PdfTemplateType.SAMMELRECHNUNG ||
		templateType === PdfTemplateType.AKONTORECHNUNG
	);
};

const isInvoiceWithQR = (templateType: PdfTemplateType) => {
	return (
		templateType === PdfTemplateType.RECHNUNG ||
		templateType === PdfTemplateType.RECHNUNGSDOPPEL ||
		templateType === PdfTemplateType.AKONTORECHNUNG ||
		templateType === PdfTemplateType.SAMMELRECHNUNG
	);
};

const buildBackgroundImageHtml = (
	config: PdfTemplateConfiguration["general"],
	templateConfig: PdfTemplateConfiguration[PdfTemplateType],
	templateType: PdfTemplateType,
	pageNum: number,
	totalPages: number
): string => {
	if (
		!config.backgroundImageEnabled ||
		!config.backgroundImageUrl ||
		templateConfig.backgroundImageEnabled === false
	) {
		return "";
	}
	if (isInvoiceWithQR(templateType) && pageNum === totalPages) {
		return "";
	}
	const firstPageOnly =
		templateConfig.backgroundImageFirstPageOnly ??
		config.backgroundImageFirstPageOnly ??
		false;
	if (firstPageOnly && pageNum !== 1) {
		return "";
	}
	return `
		<div style="position: absolute; top: 0; left: 0; width: 210mm; height: 297mm; z-index: -1; overflow: hidden;">
			<img src="${config.backgroundImageUrl}" style="width: 100%; height: 100%; object-fit: cover;" alt="" />
		</div>
	`;
};

/**
 * Creates a page template function for custom footers and background images that works with Kendo Drawing
 * The returned function will be called for each page
 * Returns an HTML string template as required by Kendo Drawing API
 * The Membership are implemented again since it needs to be plain html string
 */
export const createCustomFooterTemplate = (
	pdfTemplateConfiguration: PdfTemplateConfiguration,
	templateType: PdfTemplateType
) => {
	const config = pdfTemplateConfiguration.general;
	const templateConfig = pdfTemplateConfiguration[templateType];
	//inner function necessary to insert the footer for each page (kendo drawing api needs a function)
	return ({
		element,
		pageNum,
		totalPages,
	}: {
		element: HTMLElement;
		pageNum: number;
		totalPages: number;
	}) => {
		const backgroundHtml = buildBackgroundImageHtml(
			config,
			templateConfig,
			templateType,
			pageNum,
			totalPages
		);

		const showFooter =
			templateConfig.customFooterEnabled &&
			!(
				pageNum > 1 &&
				(config.customFooterOnlyFirstPage || isInvoice(templateType))
			);

		if (!showFooter) {
			return backgroundHtml;
		}

		const justifyContent = getJustifyContent(
			config.customFooterAlignment || "center"
		);
		const textAlign = config.customFooterAlignment;

		let contentHtml = "";

		if (config.customFooterType === "text" && config.customFooterText) {
			const cleanText = cleanHtmlContent(config.customFooterText);
			contentHtml = `
				<div style="text-align: ${textAlign}; width: 100%; display: flex; flex-direction: column; align-items: ${justifyContent};">
					<div>${cleanText}</div>
				</div>
			`;
		} else if (
			config.customFooterType === "image" &&
			config.customFooterImageUrl
		) {
			let imageWidth = "100%";
			let maxWidth = "100%";

			switch (config.customFooterImageWidth) {
				case "full":
					imageWidth = "100%";
					maxWidth = "100%";
					break;
				case "custom":
					if (config.customFooterImageCustomWidth) {
						imageWidth = `${config.customFooterImageCustomWidth}mm`;
						maxWidth = `${config.customFooterImageCustomWidth}mm`;
					}
					break;
				case "auto":
				default:
					imageWidth = "auto";
					maxWidth = "100%";
					break;
			}

			contentHtml = `
				<div style="width: 100%; height: 100%; display: flex; justify-content: ${justifyContent}; align-items: center; position: relative;">
					<img src="${
						config.customFooterImageUrl
					}" alt="Custom footer" style="height: 100%; width: ${imageWidth}; max-width: ${maxWidth}; object-fit: ${
						config.customFooterImageWidth === "full"
							? "cover"
							: "contain"
					};" />
				</div>
			`;
		}

		if (
			config.organizationsImgPosition === "bottom" &&
			!config.hideMembers
		) {
			let img = "";

			if (
				config.organizationsImgAlignment === "right" &&
				config.orgImgText
			) {
				img += `<p style="font-style: italic;">${config.orgImgText}</p>`;
			}
			if (config.showVzlsMembership) {
				img += `<img src=${vzlsLogo} height="20" alt="VZLS Logo" />`;
			}

			if (config.showSwissLabel) {
				img += `<img src=${swissLabelLogo} width="30" alt="Swiss Label Logo" />`;
			}
			if (config.showSZVLabel) {
				img += `<img src=${szvLogo} width="30" alt="SZV Label Logo" />`;
			}

			if (config.customImgUrl) {
				img += `<img src=${config.customImgUrl} width="30" alt="Custom Image" />`;
			}

			if (
				config.organizationsImgAlignment === "left" &&
				config.orgImgText
			) {
				img += `<p style="font-style: italic;">${config.orgImgText}</p>`;
			}

			contentHtml = img;
		}

		const footerHtml = `
			<div style="
				position: absolute;
				bottom: 5mm;
				left: 0mm;
				height: ${config.customFooterHeight}mm;
				display: flex;
				align-items: center;
				justify-content: ${justifyContent};
				background-color: transparent;
				font-size: 10pt;
				font-family: LiberationSans, Arial, sans-serif;
				padding: 2mm;
				box-sizing: border-box;
				z-index: 1000;
				margin-top: 10mm;
				${
					!config.customFooterIgnoreLayout
						? `padding-left: ${
								config.marginLeft + 5
							}mm; padding-right: ${config.marginRight + 5}mm;`
						: ""
				}
				width: 100%;
			">
				${contentHtml}
			</div>
		`;

		return backgroundHtml + footerHtml;
	};
};
